import type {
  DispatchParseField,
  ParsedCallFields,
} from '../../api/dispatchParse';
import {
  blankCall,
  parseVehicle,
  splitPickup,
  type DraftCall,
  type DraftPriority,
} from './parseCall';

/**
 * The backend parser's fields as the chat's call. Pure, so the mapping is
 * tested without a screen. The parse names drivers by their Driver id; the
 * chat keys drivers by user id, so `driverUserIdOf` maps one to the other.
 */

const PRIORITY: Record<ParsedCallFields['priority'], DraftPriority> = {
  NORMAL: 'NORMAL',
  HIGH: 'HIGH',
  // The create form has no EMERGENCY; URGENT is its top.
  EMERGENCY: 'URGENT',
};

/** "+14085550118" as "408-555-0118"; any other number as sent. */
export function phoneText(phone: string): string {
  const m = phone.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : phone;
}

/**
 * An ISO time as "10/12/2026 3:37 AM" in this device's zone, the form
 * fmtReceived reads. The device is the dispatcher's, in the company's zone.
 */
export function receivedText(iso: string, timeZone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  // In the company's time zone, where the text's time was read, not this
  // device's: a dispatcher travelling still sees the time the text says.
  const parts = (zone?: string) =>
    Object.fromEntries(
      new Intl.DateTimeFormat('en-US', {
        timeZone: zone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
        .formatToParts(date)
        .map((p) => [p.type, p.value])
    );
  let p: Record<string, string>;
  try {
    p = parts(timeZone);
  } catch {
    p = parts(undefined);
  }
  return `${p.month}/${p.day}/${p.year} ${p.hour}:${p.minute} ${p.dayPeriod}`;
}

export interface ReadCall {
  call: DraftCall;
  notes: string[];
  /** The account the parse matched, when it is one of `accountIds`. */
  accountId: string | null;
  /** The driver the parse named, as a user id, when it is one the chat lists. */
  driverUserId: string | null;
}

export function fromParsedFields(
  fields: ParsedCallFields,
  {
    accountIds,
    driverUserIdOf,
    timeZone,
  }: {
    accountIds: readonly string[];
    driverUserIdOf: (driverId: string) => string | null;
    timeZone?: string;
  }
): ReadCall {
  const call = blankCall();
  const pickup = fields.pickup ?? '';
  const city = fields.pickupCity ?? '';
  call.pickup = pickup && city ? `${pickup}, ${city}` : pickup || city;
  call.destination = fields.destination ?? '';
  call.year = fields.vehicle?.year ? String(fields.vehicle.year) : '';
  call.make = fields.vehicle?.make ?? '';
  call.model = fields.vehicle?.model ?? '';
  call.color = fields.vehicle?.color ?? '';
  call.plate = fields.plate ?? '';
  call.vin = fields.vin ?? '';
  call.towType = fields.weightClass ?? '';
  call.priority = PRIORITY[fields.priority] ?? 'NORMAL';
  call.contactName = fields.contactName ?? '';
  call.contactPhone = fields.contactPhone ? phoneText(fields.contactPhone) : '';
  call.dispatchNumber = fields.eventNumber ?? '';
  call.received = fields.receivedAt
    ? receivedText(fields.receivedAt, timeZone)
    : '';
  call.reason = fields.reason ?? '';
  // A sign-off like "- Kev" is the contact, not an account. When the parse
  // hands back the contact's own name as the sender, there is no sender.
  const sentAs = (fields.accountNameAsSent ?? '').trim();
  const contact = (fields.contactName ?? '').trim().toLowerCase();
  call.account =
    sentAs && contact && contact.includes(sentAs.toLowerCase()) ? '' : sentAs;

  const notes = [...(fields.notes ?? [])];
  if (fields.crossStreet) notes.unshift(`Cross street: ${fields.crossStreet}`);

  const accountId =
    fields.accountId && accountIds.includes(fields.accountId)
      ? fields.accountId
      : null;
  const driverUserId = fields.driverId ? driverUserIdOf(fields.driverId) : null;
  return { call, notes, accountId, driverUserId };
}

/** The fields the parse filled, by the backend's names: the non-empty ones. */
export function filledByAi(fields: ParsedCallFields): DispatchParseField[] {
  const filled: DispatchParseField[] = [];
  const top = [
    'pickup',
    'pickupCity',
    'crossStreet',
    'destination',
    'plate',
    'vin',
    'weightClass',
    'contactName',
    'contactPhone',
    'eventNumber',
    'receivedAt',
    'reason',
    'accountId',
    'accountNameAsSent',
    'driverId',
  ] as const;
  for (const key of top) if (fields[key]) filled.push(key);
  for (const key of ['year', 'make', 'model', 'color'] as const)
    if (fields.vehicle?.[key]) filled.push(`vehicle.${key}`);
  if (fields.priority && fields.priority !== 'NORMAL') filled.push('priority');
  if (fields.notes?.length) filled.push('notes');
  return filled;
}

/** A card text field's backend names, for each part that differs. */
export function changedOnSave(
  key:
    | 'pickup'
    | 'destination'
    | 'vehicle'
    | 'contactPhone'
    | 'reason'
    | 'dispatchNumber'
    | 'contactName'
    | 'plate'
    | 'vin',
  was: DraftCall,
  value: string
): DispatchParseField[] {
  const next = value.trim();
  if (key === 'vehicle') {
    const parts = parseVehicle(value);
    return (['year', 'make', 'model'] as const)
      .filter((part) => parts[part] !== was[part])
      .map((part) => `vehicle.${part}` as const);
  }
  if (key === 'pickup') {
    const before = splitPickup(was.pickup);
    const after = splitPickup(next);
    const changed: DispatchParseField[] = [];
    if (before.street !== after.street) changed.push('pickup');
    if (before.city !== after.city) changed.push('pickupCity');
    return changed;
  }
  if (next === was[key]) return [];
  return [key === 'dispatchNumber' ? 'eventNumber' : key];
}

/** `list` with `add` in it, each name once. */
export function withChanged(
  list: readonly DispatchParseField[],
  add: readonly DispatchParseField[]
): DispatchParseField[] {
  return add.length ? [...new Set([...list, ...add])] : [...list];
}
